import {HStack, Show, Text} from 'platform/foundation';
import {match} from 'ts-pattern';

import {ConsentRecipient, ConsentSubject, ConsentType} from '@dms/api/shared';
import i18n from '@dms/i18n';
import {useBranches} from '@dms/shared';

import {DOT_CHARACTER, EMPTY_PLACEHOLDER, Nullish} from 'shared/web';

import {ConsentRecipientInstitutions} from '../../../components/ConsentRecipientInstitutions/ConsentRecipientInstitutions';

interface ConsentTypeRecipientProps {
  consentType: ConsentType;
}

export function ConsentTypeRecipient(props: ConsentTypeRecipientProps) {
  const {getBranchById} = useBranches();

  return match(props.consentType.subject)
    .with(ConsentSubject.TENANT, () => (
      <DealerRecipient
        dealerName={i18n.t('entity.gdpr.labels.consentType.subjects.tenant')}
        thirdParty={props.consentType.recipient}
      />
    ))
    .with(ConsentSubject.BRANCH, () => (
      <DealerRecipient
        dealerName={
          getBranchById(props.consentType.branchId ?? '')?.marketingName ?? EMPTY_PLACEHOLDER
        }
        thirdParty={props.consentType.recipient}
      />
    ))
    .with(ConsentSubject.THIRD_PARTY, () => (
      <ThirdPartyRecipient recipient={props.consentType.recipient} />
    ))
    .exhaustive();
}

interface DealerRecipientProps {
  dealerName: string;
  thirdParty: ConsentRecipient | Nullish;
}

function DealerRecipient(props: DealerRecipientProps) {
  return (
    <HStack spacing={2} align="center" wrap>
      <Text size="small">{props.dealerName}</Text>
      <Show when={props.thirdParty}>
        {(thirdParty) => (
          <>
            <Text size="small">{DOT_CHARACTER}</Text>
            <ThirdPartyRecipient recipient={thirdParty} />
          </>
        )}
      </Show>
    </HStack>
  );
}

interface ThirdPartyRecipientProps {
  recipient: ConsentRecipient | Nullish;
}

function ThirdPartyRecipient(props: ThirdPartyRecipientProps) {
  return (
    <HStack spacing={2} align="center" wrap>
      <Text size="small">{props.recipient?.name ?? EMPTY_PLACEHOLDER}</Text>
      <ConsentRecipientInstitutions institutionTypes={props.recipient?.institutionTypes ?? []} />
    </HStack>
  );
}
